// 테스트 관련 코드, 추후 삭제 예정
/**
 * 공통 컴포넌트 개발자 가이드 페이지
 *
 * 이 파일은 각 에이전트 개발자가 공통 컴포넌트를 자신의 화면에
 * 붙일 때 참고하는 레퍼런스입니다.
 */
import { useState } from 'react';
import { useAuthStore } from '@indipia/shared';
import {
  ExternalLinkSend,
  SiteRegistrationPopup,
  FloorPlanRegistrationPopup,
  PartnerFormPopup,
  VendorFormPopup,
} from '@indipia/shared-features';
import { createNotification, type CreateNotiInput } from '../../app/shell/notifications/notificationApi';

const NOTI_SCENARIOS: Array<{ label: string; build: (recipientId: number) => CreateNotiInput }> = [
  {
    label: '인디포토 · 고객 URL 확인',
    build: (recipientId) => ({
      sourceAgent: 'A01',
      memberId: recipientId,
      notiType: 'EXTERNAL_URL_VIEWED',
      title: '박영희님 · 래미안 퍼스티지',
      body: '공유하신 현장 사진첩을 고객이 열람했습니다.',
      targetType: 'PHOTO_ALBUM',
      targetId: 2001,
      linkUrl: '/agents/photo-organizer',
    }),
  },
  {
    label: '인디플래너 · 반장님 일정',
    build: (recipientId) => ({
      sourceAgent: 'E01',
      memberId: recipientId,
      notiType: 'SCHEDULE_REMINDER',
      title: '최강훈 반장 · 역삼동 개나리',
      body: '최강훈 반장님이 일정을 확인했습니다.',
      targetType: 'SCHEDULE',
      targetId: 3001,
      linkUrl: '/agents/scheduler',
    }),
  },
  {
    label: '인디공사현황 · 현장보고 접수',
    build: (recipientId) => ({
      sourceAgent: 'A02',
      memberId: recipientId,
      notiType: 'SITE_REPORT_RECEIVED',
      title: '박철수 반장 · 서초동 래미안',
      body: '욕실 방수 2차 작업 완료. 24시간 양생 후 타일 시공 가능합니다.',
      targetType: 'SITE_REPORT',
      targetId: 1001,
      linkUrl: '/agents/work-log',
    }),
  },
];

// ── Props 가이드 ───────────────────────────────────────────────────────────
const PROPS_GUIDE = [
  {
    prop: 'sourceService',
    type: 'string',
    value: '"sv-indipia" 등',
    desc: '리소스를 소유한 BE 서비스명. 에이전트별로 BE 팀과 협의 필요',
  },
  {
    prop: 'sourceAgent',
    type: 'string',
    value: '"SYS" | "D01" 등',
    desc: 'Hermes 발송 출처 에이전트 코드. 시스템 발송은 "SYS", 에이전트 화면은 에이전트 코드 ex A01',
  },
  {
    prop: 'resourceType',
    type: 'string',
    value: '"WORK_LOG"',
    desc: '에이전트별 리소스 유형. BE 팀과 협의하여 결정. 예: PHOTO_GROUP, SCHEDULE, WORK_LOG',
  },
  {
    prop: 'resolveResourceRef',
    type: '() => Promise<string>',
    value: 'async () => String(workLogId)',
    desc: '발송 시점에 공유 대상 키를 확보하는 콜백. 단순 대상은 키를 그대로 반환, 복합 대상(사진 N장 등)은 자기 BE에 묶음 저장 후 묶음 키 반환',
  },
  {
    prop: 'shareType',
    type: 'string',
    value: '"WORK_LOG_SHARE" 등',
    desc: '수신자가 링크 클릭 시 열리는 랜딩 페이지 템플릿 분기키.',
  },
  {
    prop: 'shareScope',
    type: 'string',
    value: '"READ"',
    desc: '수신자 접근 권한 (READ / RESPOND / DOWNLOAD). 대부분 "READ" 사용',
  },
  {
    prop: 'onClose',
    type: '() => void',
    value: '() => setShareOpen(false)',
    desc: '닫기 버튼 또는 발송 완료 시 호출. 에이전트 화면의 공유 영역 상태를 닫음',
  },
  {
    prop: 'initialRecipients',
    type: 'Array<{name; mobile; recipientType}>?',
    value: '[{ name:"홍길동", mobile:"01012345678", recipientType:"CUSTOMER" }]',
    desc: '(선택) 진입 시 미리 채울 수신자. 사용자가 화면에서 수정·추가 가능. 미주입 시 빈 상태',
  },
  {
    prop: 'initialTitle',
    type: 'string?',
    value: '"레미안 84A 시공사진 공유"',
    desc: '(선택) 메시지 제목 기본값',
  },
  {
    prop: 'initialContent',
    type: 'string?',
    value: '"안녕하세요, 시공 사진 공유드립니다."',
    desc: '(선택) 메시지 내용 기본값',
  },
  {
    prop: 'initialExpiresAt',
    type: 'string?',
    value: '"2026-07-15"',
    desc: "(선택) 링크 유효기간 기본값 ('YYYY-MM-DD')",
  },
] as const;

// ── 에이전트 화면 적용 코드 예시 ───────────────────────────────────────────
const CODE_EXAMPLE = `// 1. import
import { ExternalLinkSend } from '@indipia/shared-features';

// 2. 에이전트 페이지 컴포넌트 내부
const [shareOpen, setShareOpen] = useState(false);

// 3. 공유 버튼 (에이전트 화면에서 원하는 위치에 배치)
<button onClick={() => setShareOpen(true)}>URL 공유</button>

// 4. 인라인 영역 (shareOpen 상태로 조건부 렌더)
{shareOpen && (
  <ExternalLinkSend
    key={workLogId}             // 대상 전환 시 컴포넌트 리셋용
    sourceService="sv-indipia"  // ← BE 팀 협의 값으로 교체
    sourceAgent="D01"           // ← BE 팀 협의 에이전트 코드로 교체
    resourceType="WORK_LOG"     // ← 에이전트별 변경
    resolveResourceRef={async () => String(workLogId)}  // ← 복합 대상은 BE 묶음 저장 후 키 반환
    shareType="WORK_LOG_SHARE"  // ← BE/기획 팀 협의 값으로 교체
    shareScope="READ"
    onClose={() => setShareOpen(false)}

    // (선택) 진입 시 기본값 주입 — 미주입 시 빈 상태로 시작
    initialRecipients={[
      { name: '홍길동', mobile: '01012345678', recipientType: 'CUSTOMER' },
    ]}
    initialTitle="시공 사진 공유"
    initialContent="안녕하세요, 시공 사진을 공유드립니다."
    initialExpiresAt="2026-07-15"
  />
)}`;

// ── 반장님 등록 팝업 코드 예시 ───────────────────────────────────────────
const PARTNER_CODE_EXAMPLE = `// 1. import
import { PartnerFormPopup } from '@indipia/shared-features';

// 2. 에이전트 페이지 컴포넌트 내부
const [partnerOpen, setPartnerOpen] = useState(false);

// 3. 반장님 등록 버튼 (에이전트 화면에서 원하는 위치에 배치)
<button onClick={() => setPartnerOpen(true)}>반장님 등록</button>

// 4. 팝업 (partnerOpen 상태로 조건부 렌더)
{partnerOpen && (
  <PartnerFormPopup
    onClose={() => setPartnerOpen(false)}
    onSuccess={(partnerId) => {   // 등록 성공 시 생성된 partnerId 전달 (선택)
      console.log('등록 완료 partnerId:', partnerId);
      refetch();                  // 반장님 목록 갱신 등 후처리
    }}
  />
)}

// ─ Props ──────────────────────────────────────────────────────
// onClose    () => void                 닫기 버튼 또는 저장 완료 시 호출
// onSuccess  (partnerId: number) => void  등록 성공 콜백 (선택). 미전달 시 닫기만 처리
//
// ─ 내부 동작 ────────────────────────────────────────────────
// - ZEU_WORK_TYPE 공통코드로 전문분야 토글 버튼 렌더 (AppStore.codes 자동 참조)
// - 사업자 있음 체크 시 사업자번호·상호명·대표자명 입력 영역 노출
// - 저장 성공 시 ['partners'] 쿼리키 자동 invalidate → 목록 자동 갱신`;

// ── 거래처 등록 팝업 코드 예시 ───────────────────────────────────────────
const VENDOR_CODE_EXAMPLE = `// 1. import
import { VendorFormPopup } from '@indipia/shared-features';

// 2. 에이전트 페이지 컴포넌트 내부
const [vendorOpen, setVendorOpen] = useState(false);

// 3. 거래처 등록 버튼 (에이전트 화면에서 원하는 위치에 배치)
<button onClick={() => setVendorOpen(true)}>거래처 등록</button>

// 4. 팝업 (vendorOpen 상태로 조건부 렌더)
{vendorOpen && (
  <VendorFormPopup
    onClose={() => setVendorOpen(false)}
    onSuccess={(vendorId) => {    // 등록 성공 시 생성된 vendorId 전달 (선택)
      console.log('등록 완료 vendorId:', vendorId);
      refetch();                  // 거래처 목록 갱신 등 후처리
    }}
  />
)}

// ─ Props ──────────────────────────────────────────────────────
// onClose    () => void                 닫기 버튼 또는 저장 완료 시 호출
// onSuccess  (vendorId: number) => void   등록 성공 콜백 (선택). 미전달 시 닫기만 처리
//
// ─ 내부 동작 ────────────────────────────────────────────────
// - vendorTypeCode는 항상 'MATERIAL' 고정 (자재 거래처)
// - ZEU_WORK_TYPE 공통코드로 거래품목 토글 버튼 렌더 (AppStore.codes 자동 참조)
// - 이메일 입력 시 형식 유효성 검사 (형식 오류 시 저장 차단)
// - 저장 성공 시 ['vendors'] 쿼리키 자동 invalidate → 목록 자동 갱신`;

// ── 현장 등록 팝업 코드 예시 ─────────────────────────────────────────────
const SITE_CODE_EXAMPLE = `// 1. import
import { SiteRegistrationPopup } from '@indipia/shared-features';

// 2. 에이전트 페이지 컴포넌트 내부
const [siteOpen, setSiteOpen] = useState(false);

// 3. 현장 등록 버튼 (에이전트 화면에서 원하는 위치에 배치)
<button onClick={() => setSiteOpen(true)}>현장 등록</button>

// 4. 팝업 (siteOpen 상태로 조건부 렌더)
{siteOpen && (
  <SiteRegistrationPopup
    agentId={agentId}              // 에이전트 ID (requiredSteps 조회에 사용)
    onClose={() => setSiteOpen(false)}
    onComplete={(siteId) => {      // 등록 완료 콜백 (선택)
      console.log('등록 완료 siteId:', siteId);
      setSiteOpen(false);
    }}
  />
)}`;

// ── 가이드 페이지 ─────────────────────────────────────────────────────────
export function TestPage() {
  const memberId = useAuthStore((s) => s.user?.memberId ?? null);
  const [demoResourceRef, setDemoResourceRef] = useState('101');
  const [demoKey, setDemoKey] = useState(0);

  // 현장 등록 팝업 상태
  const [sitePopupOpen, setSitePopupOpen] = useState(false);
  const [demoAgentId, setDemoAgentId] = useState('1');

  // 현장 등록 — 지도 단계 끄기(mapStep=false) 데모 상태
  const [siteNoMapPopupOpen, setSiteNoMapPopupOpen] = useState(false);

  // 도면 등록 팝업 상태
  const [fpPopupOpen, setFpPopupOpen] = useState(false);
  const [demoSiteId, setDemoSiteId] = useState('1');

  // 반장님 등록 팝업 상태
  const [partnerPopupOpen, setPartnerPopupOpen] = useState(false);

  // 거래처 등록 팝업 상태
  const [vendorPopupOpen, setVendorPopupOpen] = useState(false);

  // 알림 발송 테스트 상태
  const [notiBusy, setNotiBusy] = useState<number | null>(null);
  const [notiErr, setNotiErr] = useState<string | null>(null);

  function handleRefChange(v: string) {
    setDemoResourceRef(v);
    setDemoKey((k) => k + 1); // resourceRef 변경 시 컴포넌트 리셋
  }

  async function fireNoti(idx: number) {
    if (memberId == null) { setNotiErr('로그인 후 사용하세요.'); return; }
    setNotiBusy(idx);
    setNotiErr(null);
    try {
      await createNotification(NOTI_SCENARIOS[idx].build(memberId));
    } catch {
      setNotiErr('발송 실패 — BE/인증 상태를 확인하세요.');
    } finally {
      setNotiBusy(null);
    }
  }

  return (
    <div className="mx-auto max-w-2xl space-y-6 p-6">

      {/* 상단 배너 */}
      <div className="rounded-xl border border-warning bg-surface-subtle px-4 py-3">
        <p className="text-xs font-semibold text-warning">개발자 가이드 페이지 — 프로덕션 미노출</p>
        <p className="mt-0.5 text-xs text-text-secondary">
          공통 컴포넌트 사용법 레퍼런스
        </p>
      </div>

      {/* 알림 발송 테스트 */}
      <div className="rounded-xl border border-border-default bg-white p-5">
        <h2 className="mb-1 text-sm font-semibold text-text-primary">알림 발송 테스트</h2>
        <p className="mb-3 text-xs text-text-secondary">
          수신자 = 로그인 사용자 본인 → SSE 로 토스트가 돌아옵니다.
        </p>
        <div className="space-y-2">
          {NOTI_SCENARIOS.map((s, i) => (
            <button
              key={s.label}
              type="button"
              onClick={() => fireNoti(i)}
              disabled={notiBusy !== null}
              className="block w-full rounded-lg bg-point px-3 py-2 text-left text-sm font-medium text-white hover:opacity-90 disabled:opacity-50"
            >
              {notiBusy === i ? '발송 중…' : s.label}
            </button>
          ))}
          {notiErr && <p className="text-xs text-error">{notiErr}</p>}
        </div>
      </div>

      {/* ── 현장 등록 팝업 데모 ─────────────────────────────────────────── */}
      <div className="rounded-xl border border-border-default bg-white p-5">
        <h2 className="mb-1 text-sm font-semibold text-text-primary">현장 등록 팝업</h2>
        <p className="mb-4 text-xs text-text-secondary">
          SiteRegistrationPopup — 고객 등록 → 현장 등록 → 도면 등록 3단계 모달
        </p>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <label className="text-xs text-text-secondary">agentId</label>
            <input
              type="text"
              value={demoAgentId}
              onChange={(e) => setDemoAgentId(e.target.value)}
              className="w-16 rounded-lg border border-border-default px-2 py-1 font-mono text-xs outline-none focus:ring-1 focus:ring-primary-400"
            />
          </div>
          <button
            type="button"
            onClick={() => setSitePopupOpen(true)}
            className="rounded-xl bg-primary-500 px-4 py-2 text-sm font-semibold text-white hover:bg-primary-600"
          >
            현장 등록 열기
          </button>
        </div>

        {/* 현장 등록 팝업 코드 예시 */}
        <div className="mt-4">
          <p className="mb-2 text-xs font-medium text-text-secondary">에이전트 화면 적용 코드</p>
          <pre className="overflow-x-auto rounded-lg bg-primary-900 p-4 text-xs leading-relaxed text-primary-50">
            {SITE_CODE_EXAMPLE}
          </pre>
        </div>
      </div>

      {/* 현장 등록 팝업 인스턴스 */}
      {sitePopupOpen && (
        <SiteRegistrationPopup
          agentId={parseInt(demoAgentId, 10) || 1}
          onClose={() => setSitePopupOpen(false)}
          onComplete={(siteId) => {
            console.log('[TestPage] 현장 등록 완료 siteId:', siteId);
            setSitePopupOpen(false);
          }}
        />
      )}

      {/* ── 도면 등록 팝업 데모 ─────────────────────────────────────────── */}
      <div className="rounded-xl border border-border-default bg-white p-5">
        <h2 className="mb-1 text-sm font-semibold text-text-primary">도면 등록 팝업</h2>
        <p className="mb-4 text-xs text-text-secondary">
          FloorPlanRegistrationPopup — Step C 단독 진입 (siteId만 입력)
        </p>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <label className="text-xs text-text-secondary">siteId</label>
            <input
              type="text"
              value={demoSiteId}
              onChange={(e) => setDemoSiteId(e.target.value)}
              className="w-16 rounded-lg border border-border-default px-2 py-1 font-mono text-xs outline-none focus:ring-1 focus:ring-primary-400"
            />
          </div>
          <button
            type="button"
            onClick={() => setFpPopupOpen(true)}
            className="rounded-xl bg-primary-500 px-4 py-2 text-sm font-semibold text-white hover:bg-primary-600"
          >
            도면 등록 열기
          </button>
        </div>
      </div>

      {/* 도면 등록 팝업 인스턴스 */}
      {fpPopupOpen && (
        <FloorPlanRegistrationPopup
          siteId={parseInt(demoSiteId, 10) || 1}
          onClose={() => setFpPopupOpen(false)}
          onComplete={(siteId) => {
            console.log('[TestPage] 도면 등록 완료 siteId:', siteId);
            setFpPopupOpen(false);
          }}
        />
      )}

      {/* ── 반장님 등록 팝업 데모 ────────────────────────────────────────── */}
      <div className="rounded-xl border border-border-default bg-white p-5">
        <h2 className="mb-1 text-sm font-semibold text-text-primary">반장님 등록 팝업</h2>
        <p className="mb-4 text-xs text-text-secondary">
          PartnerFormPopup — 시공반장님 정보(이름·연락처·전문분야·사업자) 등록 모달.
          저장 성공 시 <code className="rounded bg-surface-subtle px-1 font-mono">['partners']</code> 쿼리 자동 invalidate.
        </p>
        <button
          type="button"
          onClick={() => setPartnerPopupOpen(true)}
          className="rounded-xl bg-primary-500 px-4 py-2 text-sm font-semibold text-white hover:bg-primary-600"
        >
          반장님 등록 열기
        </button>

        {/* 에이전트 화면 적용 코드 예시 */}
        <div className="mt-4">
          <p className="mb-2 text-xs font-medium text-text-secondary">에이전트 화면 적용 코드</p>
          <pre className="overflow-x-auto rounded-lg bg-primary-900 p-4 text-xs leading-relaxed text-primary-50">
            {PARTNER_CODE_EXAMPLE}
          </pre>
        </div>
      </div>

      {/* 반장님 등록 팝업 인스턴스 */}
      {partnerPopupOpen && (
        <PartnerFormPopup
          onClose={() => setPartnerPopupOpen(false)}
          onSuccess={(partnerId) => {
            console.log('[TestPage] 반장님 등록 완료 partnerId:', partnerId);
            setPartnerPopupOpen(false);
          }}
        />
      )}

      {/* ── 거래처 등록 팝업 데모 ────────────────────────────────────────── */}
      <div className="rounded-xl border border-border-default bg-white p-5">
        <h2 className="mb-1 text-sm font-semibold text-text-primary">거래처 등록 팝업</h2>
        <p className="mb-4 text-xs text-text-secondary">
          VendorFormPopup — 자재 거래처 정보(업체명·담당자·이메일·거래품목·메모) 등록 모달.
          vendorTypeCode는 <code className="rounded bg-surface-subtle px-1 font-mono">'MATERIAL'</code> 고정.
          저장 성공 시 <code className="rounded bg-surface-subtle px-1 font-mono">['vendors']</code> 쿼리 자동 invalidate.
        </p>
        <button
          type="button"
          onClick={() => setVendorPopupOpen(true)}
          className="rounded-xl bg-primary-500 px-4 py-2 text-sm font-semibold text-white hover:bg-primary-600"
        >
          거래처 등록 열기
        </button>

        {/* 에이전트 화면 적용 코드 예시 */}
        <div className="mt-4">
          <p className="mb-2 text-xs font-medium text-text-secondary">에이전트 화면 적용 코드</p>
          <pre className="overflow-x-auto rounded-lg bg-primary-900 p-4 text-xs leading-relaxed text-primary-50">
            {VENDOR_CODE_EXAMPLE}
          </pre>
        </div>
      </div>

      {/* 거래처 등록 팝업 인스턴스 */}
      {vendorPopupOpen && (
        <VendorFormPopup
          onClose={() => setVendorPopupOpen(false)}
          onSuccess={(vendorId) => {
            console.log('[TestPage] 거래처 등록 완료 vendorId:', vendorId);
            setVendorPopupOpen(false);
          }}
        />
      )}

      {/* ── URL 공유 (ExternalLinkSend) 데모 ─────────────────────────────── */}
      <div className="rounded-xl border border-border-default bg-white p-5">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-sm font-semibold text-text-primary">URL 공유 (ExternalLinkSend) 라이브 데모</h2>
          <div className="flex items-center gap-2">
            <label className="text-xs text-text-secondary">resourceRef (workLogId)</label>
            <input
              type="text"
              value={demoResourceRef}
              onChange={(e) => handleRefChange(e.target.value)}
              className="w-20 rounded-lg border border-border-default px-2 py-1 font-mono text-xs outline-none focus:ring-1 focus:ring-primary-400"
            />
          </div>
        </div>

        {/* ExternalLinkSend 컴포넌트 — 항상 표시. showPreview로 "미리보기" 버튼 데모 */}
        <ExternalLinkSend
          key={demoKey}
          sourceService="sv-indipia"
          sourceAgent="SYS"
          resourceType="WORK_LOG"
          resolveResourceRef={async () => demoResourceRef}
          shareType="WORK_LOG_SHARE"
          shareScope="READ"
          // 개발자 가이드 데모 — god-BFF 기본 발송경로 제거로 sendOverride가 필수가 됨.
          // 실제 발송은 도메인 api(sendPhotoShare 등)를 주입해야 하며, 이 데모에선 비활성.
          sendOverride={async () => {
            throw new Error('개발자 가이드 데모 — 실제 발송은 도메인 sendOverride 주입 필요');
          }}
          showPreview
          initialVariables={{ 현장명: '와이앤씨빌딩' }}
          // 미리보기는 호출자가 주입한 content(최종 발송 문구)를 그대로 보여준다 — 데모용 예시 문구.
          initialContent={'[와이앤씨빌딩] 오늘 작업이 시작되었습니다.\n공유 링크에서 진행 현황을 확인하실 수 있습니다.'}
          initialButtons={[{ name: '공유 일정 확인하기', resourceRef: 'DEMO' }]}
          initialRecipients={[
            { name: '홍길동', mobile: '01012345678', recipientType: 'CUSTOMER' },
          ]}
        />
      </div>

      {/* Props 설명 테이블 */}
      <div className="rounded-xl border border-border-default bg-white p-5">
        <h2 className="mb-3 text-sm font-semibold text-text-primary">Props 설명</h2>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-border-default text-left text-text-secondary">
                <th className="pb-2 pr-3 font-medium">prop</th>
                <th className="pb-2 pr-3 font-medium">type</th>
                <th className="pb-2 pr-3 font-medium">공사현황 예시값</th>
                <th className="pb-2 font-medium">설명</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border-default">
              {PROPS_GUIDE.map((row) => (
                <tr key={row.prop}>
                  <td className="py-2 pr-3 font-mono font-semibold text-primary-600">{row.prop}</td>
                  <td className="py-2 pr-3 font-mono text-text-secondary">{row.type}</td>
                  <td className="py-2 pr-3 font-mono text-text-primary">{row.value}</td>
                  <td className="py-2 text-text-secondary">{row.desc}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* 코드 예시 */}
      <div className="rounded-xl border border-border-default bg-white p-5">
        <h2 className="mb-3 text-sm font-semibold text-text-primary">에이전트 화면 적용 코드</h2>
        <pre className="overflow-x-auto rounded-lg bg-primary-900 p-4 text-xs leading-relaxed text-primary-50">
          {CODE_EXAMPLE}
        </pre>
      </div>

      {/* ── 현장등록 지도 단계 끄기 (mapStep=false) ─────────────────────────── */}
      <div className="rounded-xl border border-border-default bg-white p-5">
        <h2 className="mb-1 text-sm font-semibold text-text-primary">현장 등록 · 지도 단계 끄기</h2>
        <p className="mb-4 text-xs text-text-secondary">
          지도연계는 <code className="rounded bg-surface-subtle px-1 font-mono">mapStep</code> 기본값 true로
          모든 현장 등록에 적용됩니다. 아래는 <code className="rounded bg-surface-subtle px-1 font-mono">mapStep=&#123;false&#125;</code>로
          지도 단계를 끄고 도로명 주소 자동 지오코딩 좌표로 바로 저장하는 데모입니다.
        </p>
        <button
          type="button"
          onClick={() => setSiteNoMapPopupOpen(true)}
          className="rounded-xl bg-primary-500 px-4 py-2 text-sm font-semibold text-white hover:bg-primary-600"
        >
          지도 단계 없이 등록
        </button>
      </div>

      {/* 지도 단계 끄기 팝업 인스턴스 */}
      {siteNoMapPopupOpen && (
        <SiteRegistrationPopup
          mapStep={false}
          agentId={parseInt(demoAgentId, 10) || 1}
          onClose={() => setSiteNoMapPopupOpen(false)}
          onComplete={(siteId) => {
            console.log('[TestPage] 지도 단계 끄기 등록 완료 siteId:', siteId);
            setSiteNoMapPopupOpen(false);
          }}
        />
      )}

    </div>
  );
}
